import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionContext, Button, Card, FormGroup, Row, Col, useAccordionToggle } from 'react-bootstrap';
import { CardContainer, Heading5, Icon, TitleIcon, Validator, Form } from '../../widgets';
import { uuid } from '../../../helpers/common';
import { FormValidator } from '../../../helpers/form-validator';
import { Unit } from '../../../helpers/unit';
import { useDispatch, useSelector } from 'react-redux';
import { saveBulkStorageLocation } from '../../../actions/geo';
import { ONE_CUBIC_METER_TO_KILOGRAM, ONE_KILOGRAM_TO_HECTOGRAM } from '../../../constants';

export const StorageLocationForm = ({ shapeName, geoRegionShapeId, onHide, shapeStorageLocations, isSaving, setIsSaving }) => {

    const weightShortName = useSelector(state => state.user.settings.weightShortName);
    const [storageLocations, setStorageLocations] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [activeKey, setActivekey] = useState(null);
    const dispatch = useDispatch();
    const itemRef = useRef();

    const unit = useMemo(() => Unit.getInstance(), []);

    const getFields = () => {
        const fields = {};
        const validateData = [];

        storageLocations.forEach((location, index) => {
            const storageName = storageNameKey(index);
            const storageCapacity = storageLocationKey(index);

            fields[storageName] = location.storage_location_name;
            fields[storageCapacity] = location.storage_location_capacity;

            !location.isDeleted && validateData.push(...[
                { field: storageName, method: 'isEmpty', validWhen: false, message: 'Storage name is required' },
                {
                    field: storageCapacity,
                    method: 'isFloat',
                    validWhen: true,
                    args: [{ min: 0.01 }],
                    validateEmpty: true,
                    message: 'Storage capacity is required and greater than 0'
                }
            ]);
        });
        return { fields, validateData };
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const { fields, validateData } = getFields();

        const validator = new FormValidator([]);
        validator.setValidation(validateData);
        const validation = validator.validate({ ...fields });
        setSubmitted(true);

        if (validation.isValid) {
            setIsSaving(true);
            const formattedStorageLocations = storageLocations.filter(s => !s.isDeleted).map(s => ({
                geo_region_shape_uuid: s.geo_region_shape_uuid,
                geo_storage_location_uuid: s.geo_storage_location_uuid,
                storage_location_name: s.storage_location_name,
                is_calculate_from_measurement: s.is_calculate_from_measurement === true ? 1 : 0,
                bay_width: s.bay_width ? s.bay_width : 0,
                bay_height: s.bay_height ? s.bay_height : 0,
                bay_length: s.bay_length ? s.bay_length : 0,
                storage_location_capacity: unit.fromWeight(s.storage_location_capacity, 2),
                pipe: s.pipe ? parseInt(s.pipe) : 0
            }));

            dispatch(saveBulkStorageLocation({ geo_region_shape_uuid: geoRegionShapeId, geo_storage_locations: formattedStorageLocations }, saved => {
                onHide();
                setIsSaving(false);
            }));
        }
    };

    const getWeight = (capacity) => {
        return unit.toWeight(capacity * ONE_CUBIC_METER_TO_KILOGRAM * ONE_KILOGRAM_TO_HECTOGRAM, 2);
    };

    const handleChangeInput = (id, key, value) => {
        const newStorageLocations = storageLocations.map(e => {
            if (id === e.geo_storage_location_uuid) {
                let capacity = 0;
                if (key === "bay_width") {
                    capacity = value * e.bay_height * e.bay_length;
                    e['storage_location_capacity'] = getWeight(capacity);
                }
                else if (key === "bay_height") {
                    capacity = e.bay_width * value * e.bay_length;
                    e['storage_location_capacity'] = getWeight(capacity);
                }
                else if (key === "bay_length") {
                    capacity = e.bay_width * e.bay_height * value;
                    e['storage_location_capacity'] = getWeight(capacity);
                }
                else if (key === "is_calculate_from_measurement" && value === true) {
                    capacity = e.bay_width * e.bay_height * e.bay_length;
                    e['storage_location_capacity'] = getWeight(capacity);
                }
                e[key] = value;
            };

            return e;
        });
        setStorageLocations(newStorageLocations);
    };

    const scrollToBottom = () => {
        if (!itemRef.current) return;
        const scrollHeight = itemRef.current.scrollHeight;
        itemRef.current.scrollTo({ top: scrollHeight, behavior: "auto" });
    };

    const handleAddFields = () => {
        setSubmitted(false);
        setStorageLocations([...storageLocations, { geo_storage_location_uuid: uuid().toUpperCase(), storage_location_name: '', storage_location_capacity: '', pipe: 0, isSubmitted: false, isDeleted: false, isAdded: true }]);
        handleToggleChange(String(storageLocations.length), null);
        scrollToBottom();
    };

    const handleRemoveToggleFields = (id, status) => {
        const values = [...storageLocations];
        const value = values.find(value => value.geo_storage_location_uuid === id);
        if (value) value.isDeleted = status;
        setStorageLocations(values);
    };

    const getInitialStorageLocations = () => {
        if (Array.isArray(shapeStorageLocations)) {
            const storageLocations = JSON.parse(JSON.stringify(shapeStorageLocations));
            return storageLocations.map(({ storage_location_capacity, ...location }) =>
                ({ ...location, storage_location_capacity: unit.toWeight(storage_location_capacity, 2), isSubmitted: false, isDeleted: false }));
        }
        return [];
    };

    const handleReset = () => {
        setStorageLocations(getInitialStorageLocations());
    };

    const isFieldChanged = (value) => {
        const initialStorage = shapeStorageLocations.find(location => location.geo_storage_location_uuid === value.geo_storage_location_uuid);
        if (!initialStorage) return true;

        const { storage_location_name, storage_location_capacity } = initialStorage;
        const initialStorageCapacity = unit.toWeight((storage_location_capacity || 0), 2);
        const currentStorageCapacity = Number(value.storage_location_capacity || 0);

        return !(storage_location_name === value.storage_location_name
            && currentStorageCapacity === initialStorageCapacity);
    };

    const handleToggleChange = (eventKey, currentKey) => {
        const isOpen = eventKey !== currentKey;
        setActivekey(isOpen ? eventKey : null);
    };

    useEffect(() => {
        const updatedStorage = storageLocations.map((storage, index) => {
            const isChanged = ((activeKey ? Number(activeKey) : null) !== index) && isFieldChanged(storage);
            return { ...storage, isSubmitted: isChanged };
        });
        setStorageLocations(updatedStorage);

    }, [activeKey]);

    const storageNameKey = index => `storageLocations.${index}.storage_location_name`;
    const storageBayWidthtKey = index => `storageLocations.${index}.bay_width`;
    const storageBayHeightKey = index => `storageLocations.${index}.bay_height`;
    const storageBayLengthKey = index => `storageLocations.${index}.bay_length`;
    const storageMeasurementKey = index => `storageLocations.${index}.is_calculate_from_measurement`;
    const storageLocationKey = index => `storageLocations.${index}.storage_location_capacity`;

    useEffect(() => {
        setStorageLocations(getInitialStorageLocations());
    }, [shapeStorageLocations]);

    const validation = useMemo(() => {
        if (submitted) {
            const { fields, validateData } = getFields();

            const validator = new FormValidator([]);
            validator.setValidation(validateData);
            return validator.validate({ ...fields });
        }
        return {};
    }, [storageLocations, submitted]);

    return (
        <CardContainer className="form-container">
            <Card className="card-form">
                <Card.Header>
                    <Card.Title>
                        <Heading5>
                            <TitleIcon className="fa fa-database" />
                            <span className="heading-txt-1">Manage Storages for</span>
                            <span className="heading-txt-2">{shapeName}</span>
                        </Heading5>

                        <i className="fa fa-times close-btn" aria-hidden="true" title="close"
                            onClick={onHide} />
                    </Card.Title>
                </Card.Header>
                <Card.Body ref={itemRef} className="storage-card-body">
                    {storageLocations.length === 0 && <div className="no-data-card">{'No storages found'}</div>}

                    <Accordion activeKey={activeKey}>
                        {storageLocations.map((storage, index) => {
                            const { geo_storage_location_uuid: id, storage_location_capacity, storage_location_name, bay_width,
                                bay_height, bay_length, is_calculate_from_measurement, pipe, isSubmitted, isDeleted, isAdded } = storage;
                            const storageName = storageNameKey(index);
                            const storageCapacity = storageLocationKey(index);
                            const storageNameValidation = validation.hasOwnProperty(storageName) ? validation[storageName] : { isInvalid: false, message: '' };
                            const storageCapacityValidation = validation.hasOwnProperty(storageCapacity) ? validation[storageCapacity] : { isInvalid: false, message: '' };

                            const hasError = storageNameValidation.isInvalid || storageCapacityValidation.isInvalid; // Object.keys(error).length > 0;

                            return (
                                <Card key={id}>
                                    <Card.Header className="storage-acc-card-header">
                                        <ContextAwareToggle eventKey={String(index)} callback={handleToggleChange} >
                                            <div className='storage-form-header'>
                                                <span>
                                                    {storage_location_name}
                                                    {(storage_location_capacity || storage_location_capacity === 0) ? (
                                                        <span className="text-muted">
                                                            <span className="mx-2">|</span>
                                                            <span>{unit.formatNumber(storage_location_capacity)}</span>
                                                            <sub>{weightShortName}</sub>
                                                        </span>) : ''}
                                                </span>
                                                <div className="d-flex align-items-center">
                                                    {hasError && <span className='storage-icon-container danger'>
                                                        <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                                                    </span>}

                                                    <span className="text-muted small mr-2">
                                                        {isDeleted ? 'Removed' : (
                                                            isSubmitted ? (
                                                                isAdded ? 'Added' : (
                                                                    isFieldChanged(storage) ? 'Edited' : null
                                                                )
                                                            ) : null
                                                        )}
                                                    </span>

                                                    <span className='storage-icon-container click' onClick={() => handleRemoveToggleFields(id, !isDeleted)}>
                                                        <i className={`fa ${isDeleted ? 'fa-undo' : 'fa-trash'}`} aria-hidden="true" />
                                                    </span>
                                                </div>

                                            </div>
                                        </ContextAwareToggle>
                                    </Card.Header>
                                    <Accordion.Collapse eventKey={String(index)}>
                                        <Card.Body>
                                            <Row>
                                                <Col>
                                                    <FormGroup>
                                                        <Validator validation={storageNameValidation}>
                                                            <Form.Label>Name</Form.Label>
                                                            <Form.Control
                                                                disabled={isDeleted}
                                                                size='sm'
                                                                type="text"
                                                                autoComplete="none"
                                                                maxLength={50}
                                                                name={storageNameKey(index)}
                                                                value={storage_location_name}
                                                                onChange={event => handleChangeInput(id, 'storage_location_name', event.target.value)}
                                                            />
                                                            <Validator.Text />
                                                        </Validator>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <FormGroup>
                                                        <Validator validation={storageCapacityValidation}>
                                                            <Form.Label>Capacity ({weightShortName})</Form.Label>
                                                            <Form.Control
                                                                disabled={isDeleted || is_calculate_from_measurement}
                                                                size='sm'
                                                                type="number"
                                                                min={1}
                                                                autoComplete="none"
                                                                name={storageLocationKey(index)}
                                                                value={storage_location_capacity}
                                                                onChange={event => handleChangeInput(id, 'storage_location_capacity', event.target.value)}
                                                            />
                                                            <Validator.Text />
                                                        </Validator>
                                                    </FormGroup>
                                                </Col>
                                                <Col>
                                                    <FormGroup>
                                                        <Validator validation={storageCapacityValidation}>
                                                            <Form.Label>No. of pipes</Form.Label>
                                                            <Form.Control
                                                                size='sm'
                                                                type="number"
                                                                min={0}
                                                                max={100}
                                                                autoComplete="none"
                                                                value={pipe}
                                                                onChange={event => handleChangeInput(id, 'pipe', event.target.value)}
                                                            />
                                                            <Validator.Text />
                                                        </Validator>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row className='measurement-toggle'>
                                                <span className='text'>Pile Dimensions (W x H x L)</span>
                                                <Form.Check
                                                    disabled={isDeleted}
                                                    type="switch"
                                                    id={id}
                                                    className="cursor-pointer"
                                                    name={storageMeasurementKey(index)}
                                                    checked={is_calculate_from_measurement ? is_calculate_from_measurement : false}
                                                    onChange={event => handleChangeInput(id, 'is_calculate_from_measurement', event.target.checked)} />
                                            </Row >
                                            {is_calculate_from_measurement ? <Row className="measurement-calculation">
                                                <Col>
                                                    <FormGroup>
                                                        <Form.Label>
                                                            Width<span className='meter-text'> {" (meter)"}</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            disabled={isDeleted}
                                                            size='sm'
                                                            type="number"
                                                            autoComplete="none"
                                                            name={storageBayWidthtKey(index)}
                                                            value={bay_width ? bay_width : 0}
                                                            onChange={event => handleChangeInput(id, 'bay_width', event.target.value)}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col>
                                                    <FormGroup>
                                                        <Form.Label>
                                                            Height<span className='meter-text'>{" (meter)"}</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            disabled={isDeleted}
                                                            size='sm'
                                                            type="number"
                                                            autoComplete="none"
                                                            name={storageBayHeightKey(index)}
                                                            value={bay_height ? bay_height : 0}
                                                            onChange={event => handleChangeInput(id, 'bay_height', event.target.value)}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col>
                                                    <FormGroup>
                                                        <Form.Label>
                                                            Length<span className='meter-text'>{" (meter)"}</span>
                                                        </Form.Label>
                                                        <Form.Control
                                                            disabled={isDeleted}
                                                            size='sm'
                                                            type="number"
                                                            autoComplete="none"
                                                            name={storageBayLengthKey(index)}
                                                            value={bay_length ? bay_length : 0}
                                                            onChange={event => handleChangeInput(id, 'bay_length', event.target.value)}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row> : null}
                                            <Row>
                                                <Col>
                                                    <Button type="button" variant="primary" className="float-right mb-3 mt-2" size="sm"
                                                        onClick={() => handleToggleChange(index, index)}>
                                                        Done
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Accordion.Collapse>
                                </Card>
                            );
                        })}
                    </Accordion>
                </Card.Body>
                <Card.Footer>
                    <div>
                        <Button
                            disabled={!(validation.hasOwnProperty('isValid') ? validation.isValid : true)}
                            className="pl-0 link-btn"
                            variant="link"
                            onClick={handleAddFields}>Add Storage</Button>
                    </div>
                    <Button type="button" variant="primary" size="sm" className="float-right ml-3" onClick={handleSubmit} >
                        <Icon className="fa fa-floppy-o"></Icon> {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button type="button" variant="danger" className="float-right" size="sm"
                        onClick={handleReset}>
                        <Icon className="fa fa-refresh"></Icon> Discard
                    </Button>
                </Card.Footer>
            </Card>
        </CardContainer >
    );
};

const ContextAwareToggle = ({ children, eventKey, callback }) => {
    const currentEventKey = useContext(AccordionContext);
    const handleOnClick = useAccordionToggle(eventKey, () => {
        callback && callback(eventKey, currentEventKey);
    });
    const isOpen = currentEventKey === eventKey;

    return (
        <div className={`d-flex flex-row align-items-center arrow ${isOpen ? "arrow-open" : ""}`}>
            {children}
            <span className='float-right storage-icon-container click' onClick={handleOnClick}>
                <i className={`fa ${isOpen ? 'fa-angle-up' : 'fa-angle-down'}`} />
            </span>
        </div>
    );
};
